/**
 * migrateProgressKeys
 *
 * One-time migration utility that scans localStorage for old guided tour
 * progress keys (from the deleted OperationsGuidedTour and FinanceGuidedTour
 * components) and copies their data into the new `ta_tour_progress` prefix
 * used by the universal GuidedTour system.
 *
 * Old key patterns:
 *   operations-guided-tour-progress:{userId}:{tourScope}:{solutionKey}:{leaseId}:{mode}:{displayMode}
 *   finance-guided-tour-progress:{userId}:finance
 *
 * New key pattern:
 *   ta_tour_progress:{moduleKey}:{userId}
 *
 * The migration is idempotent — it only runs once per browser (tracked by a
 * sentinel key) and skips keys that have already been migrated.
 */

'use client';

import type { TourProgressState } from '@/config/guided-tours/types';

const MIGRATION_SENTINEL_KEY = 'ta_tour_progress_migrated_v1';
const OLD_OPS_PREFIX = 'operations-guided-tour-progress';
const OLD_FINANCE_PREFIX = 'finance-guided-tour-progress';
const NEW_PREFIX = 'ta_tour_progress';

const OLD_TO_NEW_MODULE_MAP: Record<string, string> = {
  finance: 'total-finance',
  operations: 'total-operations',
};

interface OldStoredProgress {
  verifiedStepIds?: string[];
  verifiedStepTimestamps?: Record<string, string>;
  lastActiveFlowId?: string | null;
  lastActiveObjectiveId?: string | null;
  lastActiveStepIndex?: number | null;
}

function convertOldToNew(old: OldStoredProgress): TourProgressState {
  return {
    verified_step_ids: Array.isArray(old.verifiedStepIds) ? old.verifiedStepIds : [],
    verified_step_timestamps:
      old.verifiedStepTimestamps && typeof old.verifiedStepTimestamps === 'object'
        ? old.verifiedStepTimestamps
        : {},
    last_active_flow_id: old.lastActiveFlowId ?? null,
    last_active_objective_id: old.lastActiveObjectiveId ?? null,
    last_active_step_index: old.lastActiveStepIndex ?? null,
  };
}

function parseOldOpsKey(key: string): { moduleKey: string; userId: string } | null {
  const parts = key.split(':');
  if (parts.length < 2) return null;

  const userId = parts[1] || 'guest';
  const tourScope = parts[2] || 'operations';
  const moduleKey = OLD_TO_NEW_MODULE_MAP[tourScope] ?? 'total-operations';
  return { moduleKey, userId };
}

function parseOldFinanceKey(key: string): { moduleKey: string; userId: string } | null {
  const parts = key.split(':');
  if (parts.length < 2) return null;

  const userId = parts[1] || 'guest';
  return { moduleKey: 'total-finance', userId };
}

function mergeProgress(
  existing: TourProgressState | null,
  incoming: TourProgressState,
): TourProgressState {
  if (!existing) return incoming;

  const verifiedStepIds = new Set([...existing.verified_step_ids, ...incoming.verified_step_ids]);
  const verifiedStepTimestamps = {
    ...incoming.verified_step_timestamps,
    ...existing.verified_step_timestamps,
  };

  return {
    verified_step_ids: [...verifiedStepIds],
    verified_step_timestamps: verifiedStepTimestamps,
    last_active_flow_id: incoming.last_active_flow_id ?? existing.last_active_flow_id,
    last_active_objective_id: incoming.last_active_objective_id ?? existing.last_active_objective_id,
    last_active_step_index: incoming.last_active_step_index ?? existing.last_active_step_index,
  };
}

export function migrateOldTourProgressKeys(): void {
  if (typeof window === 'undefined') return;

  try {
    if (window.localStorage.getItem(MIGRATION_SENTINEL_KEY) === '1') return;
  } catch {
    return;
  }

  const keysToMigrate: { oldKey: string; moduleKey: string; userId: string }[] = [];

  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) continue;

      if (key.startsWith(OLD_OPS_PREFIX + ':')) {
        const parsed = parseOldOpsKey(key);
        if (parsed) keysToMigrate.push({ oldKey: key, ...parsed });
      } else if (key.startsWith(OLD_FINANCE_PREFIX + ':')) {
        const parsed = parseOldFinanceKey(key);
        if (parsed) keysToMigrate.push({ oldKey: key, ...parsed });
      }
    }
  } catch {
    return;
  }

  if (keysToMigrate.length === 0) {
    try {
      window.localStorage.setItem(MIGRATION_SENTINEL_KEY, '1');
    } catch {
      // ignore
    }
    return;
  }

  let migratedCount = 0;

  for (const { oldKey, moduleKey, userId } of keysToMigrate) {
    try {
      const raw = window.localStorage.getItem(oldKey);
      if (!raw) continue;

      const parsed = JSON.parse(raw) as OldStoredProgress;
      const newProgress = convertOldToNew(parsed);
      const newKey = `${NEW_PREFIX}:${moduleKey}:${userId}`;

      const existingRaw = window.localStorage.getItem(newKey);
      let existing: TourProgressState | null = null;
      if (existingRaw) {
        try {
          existing = JSON.parse(existingRaw) as TourProgressState;
        } catch {
          // ignore parse error
        }
      }

      const merged = mergeProgress(existing, newProgress);
      window.localStorage.setItem(newKey, JSON.stringify(merged));
      window.localStorage.removeItem(oldKey);
      migratedCount++;
    } catch {
      // skip unparseable keys
    }
  }

  try {
    window.localStorage.setItem(MIGRATION_SENTINEL_KEY, '1');
  } catch {
    // ignore
  }

  if (migratedCount > 0) {
    console.info(
      `[GuidedTour] Migrated ${migratedCount} old tour progress key(s) to new prefix "${NEW_PREFIX}"`,
    );
  }
}
